/**
 * Classe abstrata base para eventos da ponte WebView -> App nativo.
 *
 * Cada evento concreto:
 *   - declara o `type` literal (string única, lida pelo app);
 *   - declara o `payload` (shape definido pelo evento);
 *   - pode sobrescrever `validate()`, `serialize()`, `beforeSend()` e `afterSend()`.
 */

import {
  isRunningInNativeApp,
  type WindowWithNativeBridge,
} from '~/utils/browserInfo';

interface SerializedEvent<T extends string, P> {
  type: T;
  payload: P;
}

export abstract class NativeEvent<T extends string = string, P = unknown> {
  /** Identificador único do evento, usado no roteamento do lado do app. */
  abstract readonly type: T;

  /** Conteúdo do evento. Cada subclasse declara seu shape. */
  abstract readonly payload: P;

  /**
   * Valida o payload antes do envio. Lance um erro para abortar.
   */
  protected validate(): void {}

  /** Serializa o evento para a string enviada via postMessage. */
  protected serialize(): string {
    const message: SerializedEvent<T, P> = {
      type: this.type,
      payload: this.payload,
    };

    return JSON.stringify(message);
  }

  /** Hook executado antes de chamar `postMessage`. */
  protected beforeSend(): void {}

  /** Hook executado após o envio bem-sucedido. */
  protected afterSend(): void {}

  /**
   * Type guard: estamos rodando dentro do WebView do app nativo?
   */
  static isNativeApp(
    win: Window | undefined = typeof window === 'undefined'
      ? undefined
      : window,
  ): win is WindowWithNativeBridge {
    return isRunningInNativeApp(win);
  }

  /**
   * Dispara o evento. Retorna `true` quando enviado ao app,
   * `false` quando estamos fora do WebView.
   */
  dispatch(): boolean {
    const win = typeof window === 'undefined' ? undefined : window;

    if (!NativeEvent.isNativeApp(win)) {
      return false;
    }

    try {
      this.validate();
      this.beforeSend();
      win.ReactNativeWebView.postMessage(this.serialize());
      this.afterSend();

      return true;
    } catch (e) {
      console.error(`[NativeBridge] falha ao disparar "${this.type}":`, e);

      return false;
    }
  }
}
